import { useState } from 'react'
import { repository } from '../../data'
import type { PersonView, View } from './derive'
import { reachText, ruleWhat } from './derive'
import { GiveAccess } from './GiveAccess'
import { ago, day } from './format'
import { Avatar, NetDot, useArm, useWrite } from './parts'

/**
 * One person: their teams (changed here), what that lets them reach, their
 * laptops, and the red button. Deactivating their Kronos account does the
 * same as the red button, on the next push.
 */
export function PersonPanel({ view, person, onClose }: { view: View; person: PersonView; onClose: () => void }): JSX.Element {
  const [giving, setGiving] = useState(false)
  const arm = useArm()
  const team = useWrite(
    (teamId: string, on: boolean) => repository.setTeamMember(teamId, person.id, on),
    (teamId, on) => `${person.name} ${on ? 'joined' : 'left'} ${view.team(teamId)?.name}${on ? '' : ' · sessions through it closed'}`,
  )
  const all = useWrite(() => repository.revokeEverything(person.id), () => `${person.name} can no longer SSH into anything · sessions closed`)
  const hide = useWrite(() => repository.setVisibility(person.id, 'hidden').then((r) => (onClose(), r)), () => `${person.name} is no longer listed in Connectivity`)
  const rules = view.rules.filter((r) => r.teamId !== null && person.teamIds.includes(r.teamId))
  const end = useWrite((ruleId: string) => repository.endRule(ruleId), (ruleId) => {
    const r = person.rules.find((x) => x.id === ruleId)
    return `${person.name} can no longer SSH into ${r ? ruleWhat(view, r) : 'it'} · sessions closed`
  })
  const outside = view.teams.filter((t) => !person.teamIds.includes(t.id))
  const anything = person.teamIds.length > 0 || person.rules.length > 0

  return (
    <section className="cx-panel cx-panel--padded" aria-label={person.name}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="row" style={{ gap: 12 }}>
          <Avatar initials={person.initials} online={person.online} size={44} />
          <span className="stack" style={{ gap: 2 }}>
            <span className="cx-title">{person.name}</span>
            <span className="muted cx-small">{person.email}</span>
          </span>
        </div>
        <button aria-label="Close" onClick={onClose}>×</button>
      </div>

      <span className="field-label">Teams</span>
      <div className="cx-chips">
        {person.teamIds.map((id) => (
          <span key={id} className="cx-chip cx-chip--cool cx-chip--removable">
            {view.team(id)?.name}
            <button
              className={arm.armed(`t${id}`) ? 'is-armed' : ''}
              aria-label={`Take ${person.name} out of ${view.team(id)?.name}`}
              onClick={() => arm.press(`t${id}`, () => team.run(id, false))}
            >
              {arm.armed(`t${id}`) ? 'Sure?' : '×'}
            </button>
          </span>
        ))}
        {outside.length ? (
          <select value="" aria-label="Add to a team" onChange={(e) => e.currentTarget.value && team.run(e.currentTarget.value, true)}>
            <option value="">+ Add to a team</option>
            {outside.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        ) : null}
      </div>

      <span className="field-label">Can reach · {reachText(person.robots, person.computers)}</span>
      <ul className="cx-list">
        {person.rules.map((r) => (
          <li key={r.id} className={`cx-reach ${r.expiresAt ? 'is-direct' : ''}`}>
            <span className="stack" style={{ gap: 2 }}>
              <span className="cx-strong">{ruleWhat(view, r)}</span>
              <span className="muted cx-small">
                their own{r.level === 'restricted' ? ' · restricted' : ''}{r.expiresAt ? ` · until ${day(r.expiresAt)}` : ''}
              </span>
            </span>
            <button className={arm.armed(`r${r.id}`) ? 'danger is-armed' : 'danger'} disabled={end.pending} onClick={() => arm.press(`r${r.id}`, () => end.run(r.id))}>
              {arm.armed(`r${r.id}`) ? 'Sure?' : 'Remove'}
            </button>
          </li>
        ))}
        {rules.map((r) => (
          <li key={r.id} className="cx-reach">
            <span className="cx-strong">{ruleWhat(view, r)}</span>
            <span className="muted cx-small">via {view.team(r.teamId ?? '')?.name}{r.level === 'restricted' ? ' · restricted' : ''}</span>
          </li>
        ))}
        {!anything ? <li className="muted">Nothing yet. Give them access, or put them in a team.</li> : null}
      </ul>

      <span className="field-label">Their devices</span>
      <ul className="cx-list">
        {person.devices.map((d) => (
          <li key={d.id} className="row" style={{ gap: 10 }}>
            <NetDot state={d.online ? 'online' : 'offline'} />
            <span className="cx-mono">{d.name}</span>
            <span className="muted cx-small">
              {d.online ? 'online now' : d.lastSeen ? `seen ${ago(d.lastSeen)}` : 'offline'}
              {d.keyExpiry ? ` · sign in again by ${day(d.keyExpiry)}` : ''}
            </span>
          </li>
        ))}
        {person.devices.length === 0 ? <li className="muted">Not signed in to the network on any device yet.</li> : null}
      </ul>

      <div className="cx-actions">
        <button onClick={() => setGiving(true)}>Give access</button>
        <button className={arm.armed('all') ? 'danger is-armed' : 'danger'} disabled={!anything || all.pending} onClick={() => arm.press('all', () => all.run())}>
          {arm.armed('all') ? 'Press again: revoke everything' : `Revoke all of ${person.name.split(' ')[0]}’s access`}
        </button>
      </div>
      <button disabled={anything || hide.pending} onClick={() => hide.run()} title={anything ? 'Take their access away first' : undefined}>
        Don’t list in Connectivity
      </button>
      {anything ? <span className="muted cx-small">Someone who can get in is always listed. Take their access away first to stop listing them.</span> : null}
      <span className="muted cx-small">Revoke all takes them out of every team and removes their own access. Open sessions close within seconds.</span>
      {giving ? <GiveAccess view={view} preset={{ userId: person.id }} onClose={() => setGiving(false)} /> : null}
    </section>
  )
}
